const iosDevices = /iPad|iPhone|iPod/.test(navigator.userAgent);
const eventUse = iosDevices ? 'touchstart' : 'click';

export const BaseSlider = class BaseSlider {
  constructor(el) {
    // DOM references
    this.list = el.querySelector('[data-slider-list]');
    this.slides = el.querySelectorAll('[data-slider-slide]');
    this.controls = el.querySelectorAll('[data-slider-control]');

    // states
    this.scrollPosition = -1;
    this._current = 0;
    this._scollsettimeout;

    // permanently bind this to methods
    this.updateScroll = this.updateScroll.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.reset = this.reset.bind(this);
    this.clear = this.clear.bind(this);
  }

  // getters
  get slidesInView() {
    return Math.floor(this.list.offsetWidth / this.slides[0].offsetWidth);
  }

  get current() {
    return this._current;
  }

  // setters
  set current(value) {
    if (value < 0 || value >= this.slides.length - (this.slidesInView - 2)) {
      return;
    }
    this._current = value;
    this.updateDOM();
  }

  //methods
  init(e = 2) {
    // toggle controls displays based on numbers of slides
    this.controls.forEach((el) => {
      if (this.slides.length < e) {
        let isActive = el.classList.contains("--active");
        if(!isActive) {
          el.style.display = 'none';
        }
      } else {
        el.style.removeProperty('display');
      }
    });

    document.addEventListener('resize', this.reset);
    // DOM updates
    this.updateDOM();
    this.updateScroll();
  }
  updateScroll() {
    requestAnimationFrame(this.onScroll);
  }
  onScroll() {
    if (this.scrollPosition !== this.list.scrollLeft) {
      // still scrolling
      this.scrollPosition = this.list.scrollLeft;
      this.list.removeEventListener('scroll', this.updateScroll);
      this.updateScroll();
    } else {
      // ended scrolling
      clearTimeout(this._scollsettimeout);
      this._scollsettimeout = setTimeout(() => {
        this.list.addEventListener('scroll', this.updateScroll);
        this.updateIndex();
      }, 1000);
    }
    this.pauseVideo(this.list);
  }

  // Updates the current index based on scroll position
  updateIndex() {
    // Look for padding offset
    const defaultOffset = this.slides[0].offsetLeft;
    const windowWidth = window.offsetWidth >= 1440 ? 1 : 0;

    // Return item index if it's position match start alignment
    const index = [...this.slides].findIndex((el) => {
      return (
        el.offsetLeft - defaultOffset ===
        Math.round(el.parentElement.scrollLeft)
      );
    });
    let scrolldata = this.list.scrollLeft + this.list.offsetWidth;
    if (index != -1) {
      // if match, update index
      this.current = index;
    } else if (Math.round(scrolldata) == this.list.scrollWidth) {
      // if no match but scrollbar reached end, update to last index candidate
      this.current =
        this.slides.length - Math.max(1, this.slidesInView - windowWidth);
    }
  }
  updateDOM() {
    // Move to current slide
    this.list.scrollTo({
      behavior: 'smooth',
      left: this.slides[this.current]?.offsetLeft,
    });
    // toggle aria hidden if not in view
    this.slides.forEach((el, i) => {
      const isHidden =
        i < this.current || i >= this._current + Math.max(1, this.slidesInView);
      el.ariaHidden = isHidden;

      const focusables = el.querySelectorAll('a, button, summary');
      focusables.forEach((el) => {
        el.tabIndex = isHidden ? -1 : 0;
      });
    });
  }

  next() {
    this.current++;
    this.pauseVideo(this.list);
  }

  prev() {
    this.current--;
    this.pauseVideo(this.list);
    
  }

  goto({ target }) {
    this.current = parseInt(target.dataset.slideIndex, 10) || 0;
  }

  pauseVideo(videElement) {
    Array.prototype.forEach.call(videElement.querySelectorAll('iframe'), player => {
      player.contentWindow.postMessage('{"event":"command", "func":"pauseVideo", "args":""}', '*');
    });
  }

  clear() {
    document.removeEventListener('resize', this.reset);
    this.list.removeEventListener('scroll', this.updateScroll);
  }

  reset() {
    this.clear();
    this.init();
  }
};

export const SliderArrows = class SliderArrows extends BaseSlider {
  constructor(el) {
    super(el);
    this.prevBtn = el.querySelector('[data-slider-control="prev"]');
    this.nextBtn = el.querySelector('[data-slider-control="next"]');

    this.prev = this.prev.bind(this);
    this.next = this.next.bind(this);
  }

  init(e) {
    this.prevBtn.addEventListener("click", this.prev);
    this.prevBtn.addEventListener("touchstart", this.prev);
    this.nextBtn.addEventListener("click", this.next);
    this.nextBtn.addEventListener("touchstart", this.next);
    super.init(e);
  }

  prev(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.prevBtn.disabled) {
      super.prev();
    }
  }

  next(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.nextBtn.disabled) {
      super.next();
    }
  }

  updateDOM() {
    super.updateDOM();
    const windowWidth = window.innerWidth >= 1440 ? 1 : 0;

    const isFirstSlide = this.current === 0;
    const isLastSlide =
      this.current >= this.slides.length - Math.max(1, this.slidesInView + windowWidth);

    this.prevBtn.disabled = isFirstSlide;
    this.nextBtn.disabled = isLastSlide;

    if (isFirstSlide && isLastSlide) {
      this.prevBtn.classList.add("hideBtn");
      this.nextBtn.classList.add("hideBtn");
    } else {
      this.prevBtn.classList.remove("hideBtn");
      this.nextBtn.classList.remove("hideBtn");
    }
  }

  clear() {
    super.clear();
    this.prevBtn.removeEventListener("click", this.prev);
    this.prevBtn.removeEventListener("touchstart", this.prev);
    this.nextBtn.removeEventListener("click", this.next);
    this.nextBtn.removeEventListener("touchstart", this.next);
  }
};

export const SliderDots = class SliderDots extends BaseSlider {
  constructor(el) {
    super(el);
    this.prevBtn = el.querySelector('[data-slider-control="prev"]');
    this.nextBtn = el.querySelector('[data-slider-control="next"]');
    if(this.prevBtn && this.nextBtn) {
      this.prev = this.prev.bind(this);
      this.next = this.next.bind(this);
    }
    this.goto = this.goto.bind(this);
  }

  init(e) {
    // bind events
    if(this.prevBtn && this.nextBtn) {
      this.prevBtn.addEventListener(eventUse, this.prev);
      this.nextBtn.addEventListener(eventUse, this.next);
    }
    this.controls.forEach((el) => {
      if (this.slides.length > 1) {
        el.addEventListener(eventUse, this.goto);
      }
    });

    super.init(e);
  }

  updateDOM() {
    super.updateDOM();
    const windowWidth = window.offsetWidth >= 1440 ? 1 : 0;      
    this.controls.forEach((el) => {
      const isCurrent = this.current === parseInt(el.dataset.slideIndex, 10);
      el.disabled = isCurrent;
      el.ariaCurrent = isCurrent ? "true" : "false";
      el.classList.toggle('--active', isCurrent);
    });
    if(this.prevBtn && this.nextBtn) { 
      this.prevBtn.disabled = this.current === 0;
      this.nextBtn.disabled =
        this.current >=
        this.slides.length - Math.max(1, this.slidesInView + windowWidth);
    }  
  }

  clear() {
    super.clear();

    this.controls.forEach((el) => {
      el.removeEventListener(eventUse, this.goto);
    });
  }

  goto(event) {
    const targetIndex = parseInt(event.currentTarget.dataset.slideIndex, 10);
    if (!isNaN(targetIndex) && targetIndex !== this.current) {
      this.moveTo(targetIndex);
    }
  }

  moveTo(index) {
    this.current = index;
    this.updateDOM();
  }
};

export const SliderThumbnail = class SliderThumbnail extends BaseSlider {
  constructor(el) {
    super(el);
    let mainslides = [];
    this.slides.forEach((data)=> {
      if(!data.classList.contains("slider-control__dot")) {
        mainslides.push(data);
      } 
    });
    this.slides = mainslides;
    this.prevBtn = el.querySelector('[data-slider-control="prev"]');
    this.nextBtn = el.querySelector('[data-slider-control="next"]');

    // thumbnailControls which not contains prev & next controls
    this.thumbnailControls = Array.from(this.controls).filter(
      (el) =>
        !el.dataset.sliderControl ||
        !['prev', 'next'].includes(el.dataset.sliderControl)
    );
    this.prev = this.prev.bind(this);
    this.next = this.next.bind(this);
    this.goto = this.goto.bind(this);
  }

  init(e) {
    // bind events
    this.prevBtn.addEventListener(eventUse, this.prev);
    this.nextBtn.addEventListener(eventUse, this.next);
    this.thumbnailControls.forEach((el) => {
      if (this.slides.length > 1) {
        el.addEventListener(eventUse, this.goto);
      }
    });

    super.init(e);
    // when only one image then show thumbnail below to picture
    if (this.slides.length == 1) {
      this.thumbnailControls.forEach((el) => {
        el.style.removeProperty('display');
      });
    }
  }

  updateDOM() {
    super.updateDOM();
    // Disable controls based on current index
    this.prevBtn.disabled = this.current === 0;
    this.nextBtn.disabled =
      this.current >= this.slides.length - Math.max(1, this.slidesInView);
    const currentIndex = this.current;
    this.thumbnailControls.forEach((el, index) => {
      const isCurrent = index === currentIndex;

      el.disabled = isCurrent;
      el.ariaCurrent = isCurrent ? 'true' : 'false';
      el.classList.toggle('--active', isCurrent);
    });
  }

  clear() {
    super.clear();
    this.prevBtn.removeEventListener(eventUse, this.prev);
    this.nextBtn.removeEventListener(eventUse, this.next);
    this.thumbnailControls.forEach((el) => {
      el.removeEventListener(eventUse, this.goto);
    });
  }
};

export const getSlider = function (el, type = 'default') {
  if (type === 'default') {
    return new SliderArrows(el);
  }
  if (type === 'dots') {
    return new SliderDots(el);
  }
  if (type === 'thumbnail') {
    return new SliderThumbnail(el);
  }
};
